import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { DollarSign, Settings, CreditCard } from 'lucide-react';
import axios from 'axios';
import { PaymentMethodIcon } from './PaymentIcons';

const PaymentMethodsConfig = () => {
  const [paymentMethods, setPaymentMethods] = useState({
    pix: false,
    debit_card: false,
    credit_card: false,
    credit_card_brands: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { API } = useContext(AuthContext);

  const creditCardBrands = [
    { id: 'visa', name: 'Visa' },
    { id: 'mastercard', name: 'Mastercard' },
    { id: 'elo', name: 'Elo' },
    { id: 'amex', name: 'American Express' },
    { id: 'hipercard', name: 'Hipercard' },
    { id: 'dinners', name: 'Diners Club' }
  ];

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/producers/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      // Set default values if not found
      setPaymentMethods({
        pix: false,
        debit_card: false,
        credit_card: false,
        credit_card_brands: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/producers/payment-methods`, paymentMethods);
      toast.success('Formas de pagamento atualizadas com sucesso!');
    } catch (error) {
      console.error('Error updating payment methods:', error);
      toast.error('Erro ao atualizar formas de pagamento');
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchChange = (method, value) => {
    setPaymentMethods(prev => ({
      ...prev,
      [method]: value
    }));
  };

  const handleCreditCardBrandChange = (brandId, checked) => {
    setPaymentMethods(prev => ({
      ...prev,
      credit_card_brands: checked
        ? [...prev.credit_card_brands, brandId]
        : prev.credit_card_brands.filter(id => id !== brandId)
    }));
  };

  const getActivePaymentMethodsCount = () => {
    let count = 0;
    if (paymentMethods.pix) count++;
    if (paymentMethods.debit_card) count++;
    if (paymentMethods.credit_card) count++;
    return count;
  };

  if (loading) {
    return (
      <Card className="card-feira">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Carregando configurações...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-feira">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            Formas de Pagamento Aceitas
          </CardTitle>
          <Badge variant="outline" className="text-green-600">
            {getActivePaymentMethodsCount()} método{getActivePaymentMethodsCount() !== 1 ? 's' : ''} ativo{getActivePaymentMethodsCount() !== 1 ? 's' : ''}
          </Badge>
        </div>
        <p className="text-sm text-gray-600">
          Configure quais formas de pagamento você aceita para facilitar as vendas
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* PIX */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <PaymentMethodIcon method="pix" className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium">PIX</h4>
              <p className="text-sm text-gray-600">Pagamento instantâneo via PIX</p>
            </div>
          </div>
          <Switch
            checked={paymentMethods.pix}
            onCheckedChange={(checked) => handleSwitchChange('pix', checked)}
            data-testid="pix-switch"
          />
        </div>

        {/* Cartão de Débito */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <PaymentMethodIcon method="debit_card" className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-medium">Cartão de Débito</h4>
              <p className="text-sm text-gray-600">Pagamento com cartão de débito</p>
            </div>
          </div>
          <Switch
            checked={paymentMethods.debit_card}
            onCheckedChange={(checked) => handleSwitchChange('debit_card', checked)}
            data-testid="debit-switch"
          />
        </div>

        {/* Cartão de Crédito */}
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <PaymentMethodIcon method="credit_card" className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-medium">Cartão de Crédito</h4>
                <p className="text-sm text-gray-600">Pagamento com cartão de crédito</p>
              </div>
            </div>
            <Switch
              checked={paymentMethods.credit_card}
              onCheckedChange={(checked) => handleSwitchChange('credit_card', checked)}
              data-testid="credit-switch"
            />
          </div>

          {paymentMethods.credit_card && (
            <div className="ml-13 pl-4 border-l-2 border-orange-200">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                Bandeiras Aceitas:
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {creditCardBrands.map((brand) => (
                  <div key={brand.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={brand.id}
                      checked={paymentMethods.credit_card_brands.includes(brand.id)}
                      onCheckedChange={(checked) => handleCreditCardBrandChange(brand.id, checked)}
                      data-testid={`brand-${brand.id}`}
                    />
                    <Label
                      htmlFor={brand.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                    >
                      <PaymentMethodIcon method="credit_card" brand={brand.id} className="w-4 h-4" />
                      {brand.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Informação importante */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Importante:</h4>
              <p className="text-sm text-blue-700">
                Essas informações serão exibidas na página do produto para que os consumidores 
                saibam quais formas de pagamento você aceita. Mantenha sempre atualizado!
              </p>
            </div>
          </div>
        </div>

        {/* Botão Salvar */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full btn-feira"
          data-testid="save-payment-methods"
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Salvando...
            </div>
          ) : (
            'Salvar Configurações'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsConfig;